import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Target, Plus, Trash2, TrendingUp, Wallet, X,
  Car, Home, Plane, Smartphone, GraduationCap, Gift, 
  PiggyBank, Briefcase, Heart, Sparkles, Check
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GOAL_ICONS = [
  { id: 'piggy-bank', icon: PiggyBank, label: 'Savings' },
  { id: 'car', icon: Car, label: 'Car' },
  { id: 'home', icon: Home, label: 'Home' },
  { id: 'plane', icon: Plane, label: 'Travel' },
  { id: 'smartphone', icon: Smartphone, label: 'Tech' },
  { id: 'graduation-cap', icon: GraduationCap, label: 'Education' },
  { id: 'gift', icon: Gift, label: 'Gift' },
  { id: 'briefcase', icon: Briefcase, label: 'Business' },
  { id: 'heart', icon: Heart, label: 'Other' },
];

const GOAL_COLORS = ['#C0975C', '#0A1F44', '#2E7D6B', '#FF6B35', '#7B2CBF', '#00A86B', '#4A6FA5'];

export default function Goals() {
  const { token, refreshUser } = useAuth();
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewModal, setShowNewModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [addAmount, setAddAmount] = useState(100);

  // New goal form
  const [newGoal, setNewGoal] = useState({
    name: '',
    target_amount: 5000,
    icon: 'piggy-bank',
    color: '#C0975C',
    deadline: ''
  });

  useEffect(() => {
    fetchGoals();
  }, []);

  const fetchGoals = async () => {
    try {
      const res = await axios.get(`${API}/goals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGoals(res.data.goals);
    } catch (error) {
      console.error('Failed to fetch goals:', error);
    } finally {
      setLoading(false);
    }
  };

  const createGoal = async () => {
    if (!newGoal.name) {
      toast.error('Please enter a goal name');
      return;
    }
    try {
      const res = await axios.post(`${API}/goals`, newGoal, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGoals([...goals, res.data.goal]);
      setShowNewModal(false);
      setNewGoal({ name: '', target_amount: 5000, icon: 'piggy-bank', color: '#C0975C', deadline: '' });
      toast.success('Goal created.');
    } catch (error) {
      toast.error('Failed to create goal');
    }
  };

  const addToGoal = async () => {
    if (!selectedGoal || addAmount <= 0) return;
    try {
      const res = await axios.put(`${API}/goals/${selectedGoal.id}`, 
        { amount_to_add: addAmount },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setGoals(goals.map(g => 
        g.id === selectedGoal.id 
          ? { ...g, current_amount: res.data.new_amount, completed: res.data.completed }
          : g
      ));
      
      setShowAddModal(false);
      setSelectedGoal(null);
      setAddAmount(100);
      
      if (res.data.completed) {
        toast.success(res.data.message);
        refreshUser();
      } else {
        toast.success(`Added R${addAmount} to your goal!`);
      }
    } catch (error) {
      toast.error('Failed to update goal');
    }
  };

  const deleteGoal = async (goalId) => {
    if (!confirm('Delete this goal?')) return;
    try {
      await axios.delete(`${API}/goals/${goalId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGoals(goals.filter(g => g.id !== goalId));
      toast.success('Goal deleted');
    } catch (error) {
      toast.error('Failed to delete goal');
    }
  };

  const getIconComponent = (iconId) => {
    const found = GOAL_ICONS.find(i => i.id === iconId);
    return found ? found.icon : PiggyBank;
  };

  const totalSaved = goals.reduce((sum, g) => sum + g.current_amount, 0);
  const totalTarget = goals.reduce((sum, g) => sum + g.target_amount, 0);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin w-8 h-8 border-2 border-[#C0975C] border-t-transparent rounded-full"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 lg:p-8 max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-[#0A1F44] flex items-center justify-center">
                <Target className="w-7 h-7 text-black" />
              </div>
              <div>
                <h1 className="text-3xl font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Savings <span className="text-[#0A1F44]">Goals</span>
                </h1>
                <p className="text-[#A3A3A3]">Track your progress to financial freedom</p>
              </div>
            </div>
            <button
              onClick={() => setShowNewModal(true)}
              className="neo-button-primary px-4 py-3 font-bold flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              New Goal
            </button>
          </div>

          {/* Overview Card */}
          {goals.length > 0 && (
            <div className="neo-card p-6 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-[#A3A3A3] text-sm">Active Goals</p>
                <p className="text-3xl font-black text-[#C0975C]">{goals.filter(g => !g.completed).length}</p>
              </div>
              <div>
                <p className="text-[#A3A3A3] text-sm">Completed</p>
                <p className="text-3xl font-black text-[#00FF88]">{goals.filter(g => g.completed).length}</p>
              </div>
              <div>
                <p className="text-[#A3A3A3] text-sm">Total Saved</p>
                <p className="text-3xl font-black">R{totalSaved.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-[#A3A3A3] text-sm">Total Target</p>
                <p className="text-3xl font-black text-[#A3A3A3]">R{totalTarget.toLocaleString()}</p>
              </div>
            </div>
          )}
        </motion.div>

        {/* Goals Grid */}
        {goals.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {goals.map((goal, i) => {
              const IconComponent = getIconComponent(goal.icon);
              const progress = Math.min((goal.current_amount / goal.target_amount) * 100, 100);
              
              return (
                <motion.div
                  key={goal.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className={`neo-card p-6 relative overflow-hidden ${goal.completed ? 'border-2 border-[#00FF88]' : ''}`}
                >
                  {/* Completed Badge */}
                  {goal.completed && (
                    <div className="absolute top-4 right-4 bg-[#00FF88] text-black px-2 py-1 text-xs font-bold flex items-center gap-1">
                      <Check className="w-3 h-3" /> DONE
                    </div>
                  )}
                  
                  {/* Icon & Name */}
                  <div className="flex items-center gap-3 mb-4">
                    <div 
                      className="w-12 h-12 flex items-center justify-center"
                      style={{ backgroundColor: goal.color }}
                    >
                      <IconComponent className="w-6 h-6 text-black" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold">{goal.name}</h3>
                      {goal.deadline && (
                        <p className="text-[#A3A3A3] text-xs">Due: {new Date(goal.deadline).toLocaleDateString()}</p>
                      )}
                    </div>
                    <button
                      onClick={() => deleteGoal(goal.id)}
                      className="p-2 text-[#A3A3A3] hover:text-red-500 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Progress */}
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-[#A3A3A3]">Progress</span>
                      <span className="font-bold">{progress.toFixed(0)}%</span>
                    </div>
                    <div className="w-full bg-white/10 h-4 overflow-hidden">
                      <motion.div 
                        initial={{ width: 0 }}
                        animate={{ width: `${progress}%` }}
                        transition={{ duration: 0.5 }}
                        className="h-4"
                        style={{ backgroundColor: goal.color }}
                      />
                    </div>
                  </div>

                  {/* Amounts */}
                  <div className="flex justify-between items-end">
                    <div>
                      <p className="text-[#A3A3A3] text-xs">Saved</p>
                      <p className="text-2xl font-black" style={{ color: goal.color }}>
                        R{goal.current_amount.toLocaleString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-[#A3A3A3] text-xs">Target</p>
                      <p className="text-lg font-bold text-[#A3A3A3]">
                        R{goal.target_amount.toLocaleString()}
                      </p>
                    </div>
                  </div>

                  {/* Add Money Button */}
                  {!goal.completed && (
                    <button
                      onClick={() => {
                        setSelectedGoal(goal);
                        setShowAddModal(true);
                      }}
                      className="w-full mt-4 py-3 bg-white/10 hover:bg-white/20 transition-colors font-bold flex items-center justify-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Add Money
                    </button>
                  )}
                </motion.div>
              );
            })}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="neo-card p-12 text-center"
          >
            <Target className="w-20 h-20 mx-auto mb-4 text-[#0A1F44]" />
            <h3 className="text-2xl font-bold mb-2">No Goals Yet</h3>
            <p className="text-[#A3A3A3] mb-6">Set your first savings goal and start building your future!</p>
            <button
              onClick={() => setShowNewModal(true)}
              className="neo-button-primary px-6 py-3 font-bold"
            >
              Create Your First Goal
            </button>
          </motion.div>
        )}

        {/* New Goal Modal */}
        <AnimatePresence>
          {showNewModal && (
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="neo-card p-6 max-w-md w-full max-h-[90vh] overflow-y-auto"
              >
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold">Create New Goal</h2>
                  <button onClick={() => setShowNewModal(false)} className="p-2 hover:bg-white/10">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-6">
                  {/* Goal Name */}
                  <div>
                    <label className="block text-sm text-[#A3A3A3] mb-2">Goal Name</label>
                    <input
                      type="text"
                      value={newGoal.name}
                      onChange={(e) => setNewGoal({...newGoal, name: e.target.value})}
                      placeholder="e.g., New Laptop, Emergency Fund"
                      className="w-full bg-white/5 border border-white/10 px-4 py-3 focus:border-[#C0975C] focus:outline-none"
                    />
                  </div>

                  {/* Target Amount */}
                  <div>
                    <label className="block text-sm text-[#A3A3A3] mb-2">Target Amount: R{newGoal.target_amount.toLocaleString()}</label>
                    <input
                      type="range"
                      min="500"
                      max="100000"
                      step="500"
                      value={newGoal.target_amount}
                      onChange={(e) => setNewGoal({...newGoal, target_amount: Number(e.target.value)})}
                      className="w-full accent-[#C0975C]"
                    />
                  </div>

                  {/* Icon Selection */}
                  <div>
                    <label className="block text-sm text-[#A3A3A3] mb-2">Icon</label>
                    <div className="grid grid-cols-5 gap-2">
                      {GOAL_ICONS.map((item) => (
                        <button
                          key={item.id}
                          onClick={() => setNewGoal({...newGoal, icon: item.id})}
                          className={`p-3 flex flex-col items-center gap-1 transition-all ${
                            newGoal.icon === item.id ? 'bg-[#C0975C] text-black' : 'bg-white/5 hover:bg-white/10'
                          }`}
                        >
                          <item.icon className="w-5 h-5" />
                          <span className="text-xs">{item.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Color Selection */}
                  <div>
                    <label className="block text-sm text-[#A3A3A3] mb-2">Color</label>
                    <div className="flex gap-2">
                      {GOAL_COLORS.map((color) => (
                        <button
                          key={color}
                          onClick={() => setNewGoal({...newGoal, color})}
                          className={`w-10 h-10 ${newGoal.color === color ? 'ring-2 ring-white ring-offset-2 ring-offset-[#061530]' : ''}`}
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Deadline */}
                  <div>
                    <label className="block text-sm text-[#A3A3A3] mb-2">Deadline (Optional)</label>
                    <input
                      type="date"
                      value={newGoal.deadline}
                      onChange={(e) => setNewGoal({...newGoal, deadline: e.target.value})}
                      className="w-full bg-white/5 border border-white/10 px-4 py-3 focus:border-[#C0975C] focus:outline-none"
                    />
                  </div>

                  <button
                    onClick={createGoal}
                    className="neo-button-primary w-full py-4 font-bold"
                  >
                    Create Goal
                  </button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Add Money Modal */}
        <AnimatePresence>
          {showAddModal && selectedGoal && (
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="neo-card p-6 max-w-md w-full"
              >
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold">Add to "{selectedGoal.name}"</h2>
                  <button onClick={() => setShowAddModal(false)} className="p-2 hover:bg-white/10">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="mb-6">
                  <p className="text-[#A3A3A3] mb-2">
                    Current: R{selectedGoal.current_amount.toLocaleString()} / R{selectedGoal.target_amount.toLocaleString()}
                  </p>
                  <p className="text-sm text-[#A3A3A3]">
                    Need R{(selectedGoal.target_amount - selectedGoal.current_amount).toLocaleString()} more to reach your goal
                  </p>
                </div>

                <div className="mb-6">
                  <label className="block text-sm text-[#A3A3A3] mb-2">Amount to Add: R{addAmount.toLocaleString()}</label>
                  <input
                    type="range"
                    min="50"
                    max={Math.max(selectedGoal.target_amount - selectedGoal.current_amount, 100)}
                    step="50"
                    value={addAmount}
                    onChange={(e) => setAddAmount(Number(e.target.value))}
                    className="w-full accent-[#C0975C]"
                  />
                  <div className="flex gap-2 mt-3">
                    {[100, 500, 1000].map((amt) => (
                      <button
                        key={amt}
                        onClick={() => setAddAmount(amt)}
                        className="flex-1 py-2 bg-white/10 hover:bg-white/20 font-bold"
                      >
                        R{amt}
                      </button>
                    ))}
                  </div>
                </div>

                <button
                  onClick={addToGoal}
                  className="neo-button-primary w-full py-4 font-bold"
                >
                  Add R{addAmount.toLocaleString()}
                </button>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </Layout>
  );
}
